import { AnnotationContextGroup, Annotations as TypeAnnotations, isAnnotationContextGroup } from '../../../../../contexts/AnnotationContext';
import { GroupType } from '../../../../../types/graphqlTypes';

// ============================================================================
// Types
// ============================================================================

export type AnnotationsWithIndicesGroup = Omit<AnnotationContextGroup, 'annotations'> & {
  /** Ordered children with indices: annotations plus nested groups (a floor's ROOM folders). */
  annotations: ((TypeAnnotations[0] | AnnotationsWithIndicesGroup) & { index: number })[];
};
export type AnnotationsWithIndices = ((AnnotationsWithIndicesGroup | TypeAnnotations[0]) & { index: number })[];

export interface DropPosition {
  toIndex: number;
  toGroupIdentifier: string | undefined;
}

export type DropTarget = DropPosition | { error: string };

export type DestData =
  | {
      type: 'annotation' | 'group';
      identifier: string;
      groupIdentifier?: string;
      index: number;
    }
  | undefined;

export type DragSourceData = {
  type: 'annotation' | 'group';
  identifier: string;
  groupIdentifier?: string;
  index: number;
};

export type PointerZone = 'top' | 'bottom';

export type SourcePosition = { index: number; groupId?: string };

/** Sketch annotation types that may live inside a ROOM folder. */
const FOLDERABLE_TYPES = new Set(['SKETCH_OBJECT', 'OPENING_SKETCH']);

// ============================================================================
// Tree helpers (the tree is floor > room-folder > annotation, max two levels)
// ============================================================================

/** Find a group node anywhere in the tree (root or nested). */
export const findGroupById = (annotations: AnnotationsWithIndices, identifier: string): AnnotationsWithIndicesGroup | null => {
  for (const item of annotations) {
    if (!isAnnotationContextGroup(item)) continue;
    if (item.identifier === identifier) return item;
    for (const child of item.annotations) {
      if (isAnnotationContextGroup(child) && child.identifier === identifier) return child;
    }
  }
  return null;
};

/**
 * The FLOOR context a group belongs to: a floor resolves to itself, a nested
 * ROOM folder to its parent floor, root/unknown to undefined.
 */
const resolveFloorContextId = (annotations: AnnotationsWithIndices, groupId: string | undefined): string | undefined => {
  if (!groupId) return undefined;
  const group = findGroupById(annotations, groupId);
  if (!group) return undefined;
  if (group.parentIdentifier) return group.parentIdentifier;
  return group.type === GroupType.Floor ? group.identifier : undefined;
};

// ============================================================================
// Functions
// ============================================================================

/**
 * Computes the source item's position within its parent context (root or group).
 */
export const computeSourcePosition = (sourceData: DragSourceData, indexedAnnotations: AnnotationsWithIndices): SourcePosition => {
  if (sourceData.groupIdentifier) {
    const group = findGroupById(indexedAnnotations, sourceData.groupIdentifier);
    return {
      index: group ? group.annotations.findIndex((a) => a.identifier === sourceData.identifier) : 0,
      groupId: sourceData.groupIdentifier,
    };
  }
  return { index: indexedAnnotations.findIndex((a) => a.identifier === sourceData.identifier) };
};

/**
 * Resolves pointer position to edge zones (above/below the list).
 */
export const resolvePointerZone = (pointerY: number, listEl: HTMLElement | null): PointerZone | undefined => {
  if (!listEl) return undefined;
  const rect = listEl.getBoundingClientRect();
  if (pointerY < rect.top) return 'top';
  if (pointerY > rect.bottom) return 'bottom';
  return undefined;
};

export interface AnnotationMeta {
  annotationType: string | undefined;
  groupId: string | undefined;
  groupType: GroupType | undefined;
  /** Set when the identifier is itself a group (e.g. a dragged ROOM folder). */
  ownGroupType?: GroupType;
}

/**
 * Helper to find an annotation OR nested group and its metadata (type, parent
 * group id/type). Walks the nested level too.
 */
export const findAnnotationMeta = (annotations: AnnotationsWithIndices, identifier: string): AnnotationMeta | null => {
  for (const item of annotations) {
    if (!isAnnotationContextGroup(item)) {
      if (item.identifier === identifier) return { annotationType: item.type, groupId: undefined, groupType: undefined };
      continue;
    }
    if (item.identifier === identifier) return { annotationType: undefined, groupId: item.parentIdentifier ?? undefined, groupType: undefined, ownGroupType: item.type };
    for (const child of item.annotations) {
      if (child.identifier !== identifier) continue;
      if (isAnnotationContextGroup(child)) {
        return { annotationType: undefined, groupId: item.identifier, groupType: item.type, ownGroupType: child.type };
      }
      return { annotationType: child.type, groupId: item.identifier, groupType: item.type };
    }
    for (const child of item.annotations) {
      if (!isAnnotationContextGroup(child)) continue;
      const grandChild = child.annotations.find((a) => a.identifier === identifier);
      if (grandChild) return { annotationType: grandChild.type, groupId: child.identifier, groupType: child.type };
    }
  }
  return null;
};

/**
 * Validates a move against the nesting rules. Throws on violation:
 * - ROOM/WALL_SKETCH annotations stay in their floor (and never in a folder —
 *   calculateDropPosition redirects those to the floor before validation).
 * - ROOM folders only reorder within their own parent floor.
 * - Sketches may move between a floor and its room folders, never across floors.
 */
export const validateMove = ({ destGroupId, selectedIdentifiers, annotations }: { destGroupId: string | undefined; selectedIdentifiers: { type: string; identifier: string }[]; annotations: AnnotationsWithIndices }): void => {
  const destGroup = destGroupId ? findGroupById(annotations, destGroupId) : null;
  const destIsRoomFolder = destGroup?.type === GroupType.Room;
  const destFloorId = resolveFloorContextId(annotations, destGroupId);

  for (const { identifier } of selectedIdentifiers) {
    const item = findAnnotationMeta(annotations, identifier);
    if (!item) continue;

    // A dragged ROOM folder may only land inside its own parent floor.
    if (item.ownGroupType === GroupType.Room) {
      if (destGroupId !== item.groupId) {
        throw new Error('ROOM_FOLDER_MUST_STAY_IN_FLOOR');
      }
      continue;
    }

    const sourceFloorId = item.groupType === GroupType.Floor ? item.groupId : item.groupType === GroupType.Room ? resolveFloorContextId(annotations, item.groupId) : undefined;

    // Floor-locked annotation types cannot leave their floor subtree.
    const isLeavingFloor = sourceFloorId && sourceFloorId !== destFloorId;
    if (isLeavingFloor) {
      if (item.annotationType === 'ROOM') {
        throw new Error('ROOM_MUST_STAY_IN_FLOOR');
      }
      if (item.annotationType === 'WALL_SKETCH') {
        throw new Error('SKELETON_MUST_STAY_IN_FLOOR');
      }
      // Sketches move freely within their floor subtree (floor ↔ folders),
      // but never into another floor's subtree.
      if (FOLDERABLE_TYPES.has(item.annotationType ?? '') && destFloorId) {
        throw new Error('SKETCH_MUST_STAY_IN_FLOOR');
      }
    }

    // Only sketches may sit INSIDE a room folder.
    if (destIsRoomFolder && !FOLDERABLE_TYPES.has(item.annotationType ?? '')) {
      throw new Error('ONLY_SKETCHES_IN_ROOM_FOLDER');
    }
  }
};

/**
 * Calculates the drop position based on card center vs target center.
 * Returns the visual index where the indicator should appear.
 */
export const calculateDropPosition = (
  cardCenterY: number,
  overData: DestData,
  overRect: { top: number; height: number },
  selectedIds: { type: string; identifier: string }[],
  indexedAnnotations: AnnotationsWithIndices,
  openGroups: Set<string>,
): DropPosition => {
  // Guard against undefined overData
  if (!overData || !overData.identifier) {
    return { toIndex: indexedAnnotations.length, toGroupIdentifier: undefined };
  }

  const overCenterY = overRect.top + overRect.height / 2;
  const isCardAboveTarget = cardCenterY < overCenterY;
  const draggedGroups = selectedIds.filter(({ type }) => type === 'group').map(({ identifier }) => findGroupById(indexedAnnotations, identifier));
  const isDraggingGroup = draggedGroups.length > 0;
  // ROOM folders order within their parent floor's context; other groups at root.
  const isDraggingRoomFolder = isDraggingGroup && draggedGroups.every((g) => g?.type === GroupType.Room);

  /** The context (parent id or root) and child list a group row orders in. */
  const contextOfGroup = (group: AnnotationsWithIndicesGroup): { contextId: string | undefined; items: { identifier: string }[] } => {
    if (group.parentIdentifier) {
      const parent = findGroupById(indexedAnnotations, group.parentIdentifier);
      if (parent) return { contextId: parent.identifier, items: parent.annotations };
    }
    return { contextId: undefined, items: indexedAnnotations };
  };

  let toIndex: number;
  let toGroupIdentifier: string | undefined;

  // Handle group header as target
  if (overData.type === 'group') {
    const overGroup = findGroupById(indexedAnnotations, overData.identifier);
    if (!overGroup) {
      return { toIndex: indexedAnnotations.length, toGroupIdentifier: undefined };
    }
    const { contextId: overContextId, items: overContextItems } = contextOfGroup(overGroup);
    const groupPosInContext = overContextItems.findIndex((a) => a.identifier === overGroup.identifier);

    if (isCardAboveTarget || isDraggingGroup) {
      // Dropping above the group header (or dragging a group) -> place in the
      // header's own context before/after the group row.
      toIndex = isCardAboveTarget ? groupPosInContext : groupPosInContext + 1;
      toGroupIdentifier = overContextId;
    } else {
      // Dropping below group header -> enter group at position 0 (if open)
      if (openGroups.has(overData.identifier)) {
        toIndex = 0;
        toGroupIdentifier = overData.identifier;
      } else {
        // Group is closed -> place after the group row in its own context
        toIndex = groupPosInContext + 1;
        toGroupIdentifier = overContextId;
      }
    }
  }
  // Handle annotation (or nested folder row) inside a group
  else if (overData.groupIdentifier) {
    if (isDraggingGroup && !isDraggingRoomFolder) {
      // Non-nestable groups resolve to root, after the root group containing the target.
      const floorId = resolveFloorContextId(indexedAnnotations, overData.groupIdentifier) ?? overData.groupIdentifier;
      const parentGroupRootIndex = indexedAnnotations.findIndex((a) => isAnnotationContextGroup(a) && a.identifier === floorId);
      toIndex = parentGroupRootIndex === -1 ? indexedAnnotations.length : parentGroupRootIndex + 1;
      toGroupIdentifier = undefined;
    } else {
      const group = findGroupById(indexedAnnotations, overData.groupIdentifier);
      if (group) {
        const targetPositionInGroup = group.annotations.findIndex((a) => a.identifier === overData.identifier);

        if (targetPositionInGroup === -1) {
          // Fallback: place at end of group
          toIndex = group.annotations.length;
          toGroupIdentifier = overData.groupIdentifier;
        } else {
          // Place before or after the target within the group
          toIndex = isCardAboveTarget ? targetPositionInGroup : targetPositionInGroup + 1;
          toGroupIdentifier = overData.groupIdentifier;
        }
      } else {
        toIndex = 0;
        toGroupIdentifier = overData.groupIdentifier;
      }
    }
  }
  // Handle root-level annotation
  else {
    const targetPositionInRoot = indexedAnnotations.findIndex((a) => a.identifier === overData.identifier);
    if (targetPositionInRoot === -1) {
      // Fallback: place at end of root
      toIndex = indexedAnnotations.length;
    } else {
      toIndex = isCardAboveTarget ? targetPositionInRoot : targetPositionInRoot + 1;
    }
    toGroupIdentifier = undefined;
  }

  // Non-sketch annotations aimed at a room folder resolve to the folder's
  // parent floor (right after the folder row) instead of erroring — folders
  // only hold sketches.
  if (toGroupIdentifier && !isDraggingGroup) {
    const destGroup = findGroupById(indexedAnnotations, toGroupIdentifier);
    if (destGroup?.type === GroupType.Room) {
      const hasNonSketch = selectedIds.some(({ identifier }) => {
        const meta = findAnnotationMeta(indexedAnnotations, identifier);
        return meta && !FOLDERABLE_TYPES.has(meta.annotationType ?? '');
      });
      if (hasNonSketch && destGroup.parentIdentifier) {
        const parent = findGroupById(indexedAnnotations, destGroup.parentIdentifier);
        const folderPos = parent ? parent.annotations.findIndex((a) => a.identifier === destGroup.identifier) : -1;
        toGroupIdentifier = destGroup.parentIdentifier;
        toIndex = folderPos === -1 ? (parent?.annotations.length ?? 0) : folderPos + 1;
      }
    }
  }

  // Validate the nesting/floor rules for the final destination.
  validateMove({
    destGroupId: toGroupIdentifier,
    selectedIdentifiers: selectedIds,
    annotations: indexedAnnotations,
  });

  return { toIndex, toGroupIdentifier };
};

/**
 * Converts a visual drop index into the insertion index the backend expects.
 *
 * The backend algorithm is: remove all moved items → renormalize the remaining
 * items in the target context (root or group) from 0 → insert at `toIndex`.
 * So the frontend must subtract the number of moved items that currently sit
 * before `visualIndex` in the target context.
 */
/**
 * Map an insertion index computed on the VISIBLE (tab-filtered) context list
 * to the same insertion point in the FULL context list: insert before the
 * visible item at `visibleIndex`, or after the LAST visible item when
 * dropping at the end. Hidden rows therefore never shift a drop, and
 * end-of-list drops land after the last row the user can actually see.
 */
export const toFullContextIndex = (
  visibleIndex: number,
  targetGroupId: string | undefined,
  visibleAnnotations: AnnotationsWithIndices,
  fullAnnotations: AnnotationsWithIndices,
): number => {
  const visibleItems: { identifier: string }[] = targetGroupId ? (findGroupById(visibleAnnotations, targetGroupId)?.annotations ?? []) : visibleAnnotations;
  const fullItems: { identifier: string }[] = targetGroupId ? (findGroupById(fullAnnotations, targetGroupId)?.annotations ?? []) : fullAnnotations;
  if (visibleIndex < visibleItems.length) {
    const anchor = visibleItems[visibleIndex];
    const fullIndex = fullItems.findIndex((item) => item.identifier === anchor.identifier);
    if (fullIndex !== -1) return fullIndex;
  }
  const lastVisible = visibleItems[visibleItems.length - 1];
  if (!lastVisible) return fullItems.length;
  const lastFullIndex = fullItems.findIndex((item) => item.identifier === lastVisible.identifier);
  return lastFullIndex === -1 ? fullItems.length : lastFullIndex + 1;
};

export const computeInsertionIndex = (
  visualIndex: number,
  targetGroupId: string | undefined,
  movedIdentifiers: Set<string>,
  indexedAnnotations: AnnotationsWithIndices,
): number => {
  let contextItems: { identifier: string }[];

  if (targetGroupId) {
    const group = findGroupById(indexedAnnotations, targetGroupId);
    contextItems = group ? group.annotations : [];
  } else {
    contextItems = indexedAnnotations;
  }

  let movedBeforeCount = 0;
  for (let i = 0; i < visualIndex && i < contextItems.length; i++) {
    if (movedIdentifiers.has(contextItems[i].identifier)) {
      movedBeforeCount++;
    }
  }

  return visualIndex - movedBeforeCount;
};
